import { useEffect, useState } from 'react';
import { api } from '../api.js';

// T285 — does your role list actually reach your field?
//
// `titleMatchesRoles` is containment of a target PHRASE: precise, and therefore
// narrow. Measured on live data, one account's ten marketing phrases reached 41
// of the marketing-titled jobs in scope while another's thirty-six reached most
// of theirs. The narrow one was not broken — it was under-configured, and
// nothing anywhere said so. The relevance shelf already follows the rule this
// borrows: hiding rows without saying how many is how a filter turns into a lie,
// and a filter that never fires tells it by omission.
//
// The sentence names the WORD it measured, never "your field". "191 of the 499
// postings whose title mentions marketing" is a claim the operator can check
// against the examples underneath it; "38% of your field" is one he cannot.
export default function RoleCoverage({ savedAt }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get('/profile/role-coverage');
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
    // T287 — keyed on `savedAt`, NOT on the count of the chips on screen. The
    // first version re-fetched on every unsaved edit — so the number answered a
    // role list the server had never seen — and did NOT re-fetch after a save
    // that only changed a phrase without changing the count. Both directions
    // wrong from one dependency.
  }, [savedAt]);

  if (failed || !data || data.coveragePct === null || !data.tokens?.length) return null;

  const tight = data.coveragePct >= 80;
  return (
    <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-xs text-slate-300">
        Your roles reach{' '}
        <span className={`font-semibold tabular-nums ${tight ? 'text-emerald-300' : 'text-amber-300'}`}>
          {data.reached}
        </span>{' '}
        of the <span className="font-semibold tabular-nums text-slate-200">{data.inField}</span> live
        postings whose title mentions{' '}
        {data.tokens.map((t, i) => (
          <span key={t}>
            {i > 0 && (i === data.tokens.length - 1 ? ' or ' : ', ')}
            <em className="text-slate-200">{t}</em>
          </span>
        ))}
        .
      </p>
      {data.nearMissed > 0 && (
        <>
          <p className="mt-1 text-[11px] text-slate-500">
            {data.nearMissed} it does not — add a phrase below and they come in on the next scan.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {data.examples.slice(0, 5).map((e) => (
              <li key={e.title} className="truncate text-[11px] text-slate-400">
                {e.title}
                {e.count > 1 && <span className="text-slate-600"> ×{e.count}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
